/**
 * API: Swym
 * ------------------------------------------------------------------------------
 * Universal wrapper to use the Swym SDK.
 * - https://developers.swym.it/docs/swym-guides-landing
 * @namespace Swym
 */
window.SwymCallbacks = window.SwymCallbacks || []
window.MintSwat = window.MintSwat || {}

/**
 * Create a new Swym SDK object.
 */
export default () => {

  /**
   * Initialise SDK.
   * - https://developers.swym.it/docs/swymcallbacks
   */
  function init() {
    window.SwymCallbacks.push(setDefaultState)
  }

  /**
   * Set the default state.
   */
  function setDefaultState(swat) {

    /**
     * Hoist state to global object so that when this API module
     * is imported elsewhere, the Swym SDK is deemed as 'Ready'.
     * - Let's try and avoid overdoing it with the global window objects.
     */
    MintSwat = swat

    mint.EventBus.dispatch('swym:ready')
  }

  /**
   * Retrieve the details and products of a given Wishlist.
   * - https://developers.swym.it/docs/fetch-lists
   * @return {Object}
   */
  function getWishlist() {
    return new Promise((resolve, reject) => {
      if (!MintSwat) {
        mint.Log('warning', '[Mint] Swym SDK not yet initialised: getWishlist')
        resolve(false)
      }

      MintSwat.fetchLists({
        callbackFn: (lists) => {

          /**
           * If the user does not have an associated wishlist, create one.
           */
          if (!lists?.length) {
            MintSwat.createList(
              { lname: 'My Wishlist' },
              response => {
                mint.services.swym.listId = response.lid
                resolve([])
              },
              error => {
                reject(error)
              },
            )
          } else {
            mint.services.swym.listId = lists[0].lid
            resolve(lists[0].listcontents)
          }
        },
        errorFn: (error) => {
          reject(error)
        },
      })
    })
  }

  /**
   * Add a given product to the default Wishlist.
   * @param {Object} product - Formatted product object.
   * @param {Object} variant - Formatted variant object.
   * @returns {Promise}
   */
  function addToWishlist(product, variant) {
    return new Promise((resolve, reject) => {
      if (!MintSwat) {
        mint.Log('warning', '[Mint] Swym SDK not yet initialised: addToWishlist')

        resolve(false)
      }

      const onSuccess = (response) => {
        if (response?.hasError) {
          if (mint.flags.dev.enableDebug) {
            console.log('[Mint] Swym API returned an error when adding to Wishlist', {
              response,
              product: product.handle,
            })
          }

          reject(response?.error)
          return
        }

        if (mint.flags.dev.enableDebug) {
          console.log('[Mint] Successfully added to Wishlist', {
            response,
            product: product.handle,
          })
        }

        resolve(response)
      }

      const onError = (error) => {
        reject(error)
      }

      MintSwat.addToList(
        mint.services.swym.listId,
        getFormattedProduct(product, variant),
        onSuccess,
        onError,
      )
    })
  }

  /**
   * Remove a product from a given list.
   * - https://developers.swym.it/docs/delete-from-list
   * @param {Object} product - Formatted product object.
   * @param {Object} variant - Formatted variant object.
   * @param {String} lid - Optional override for List ID.
   * @returns {Promise}
   */
  function removeFromWishlist(product, variant, lid = mint.services.swym.listId) {
    return new Promise((resolve, reject) => {
      if (!MintSwat) {
        mint.Log('warning', '[Mint] Swym SDK not yet initialised: removeFromWishlist')

        resolve(false)
      }

      const onSuccess = (response) => {
        if (response?.hasError) {
          if (mint.flags.dev.enableDebug) {
            console.log('[Mint] Swym API returned an error when removing from Wishlist', {
              response,
              product: product.handle,
            })
          }

          reject(response?.error)
          return
        }

        if (mint.flags.dev.enableDebug) {
          console.log('[Mint] Successfully removed from Wishlist', {
            response,
            product: product.handle,
          })
        }

        resolve(response)
      }

      const onError = (error) => {
        reject(error)
      }

      MintSwat.deleteFromList(
        lid,
        getFormattedProduct(product, variant),
        onSuccess,
        onError,
      )
    })
  }

  /**
   * Returns a formatted product object for Swym.
   * - https://developers.swym.it/docs/add-to-list#product
   * @param {Object} product - Formatted product object.
   * @param {Object} variant - Formatted variant object.
   * @returns {Boolean|Object}
   */
  function getFormattedProduct(product, variant) {
    if (!product || !variant) {
      return false
    }

    const productUrl = product.url.startsWith(location.protocol)
      ? product.url
      : `${location.protocol}${product.url}`

    const formattedProduct = {
      epi: Number(variant.id),
      empi: Number(product.id),
      du: productUrl,
    }

    return formattedProduct
  }

  /**
   * Trigger the custom  'Back in Stock' popup.
   * @param {Object} product - Formatted product object.
   * @param {Object} variant - Formatted variant object.
   * @returns {Promise}
   */
  function openBackInStock(product, variant) {
    mint.EventBus.dispatch('product:openBackInStock', {
      product,
      variant,
    })
  }

  /**
   * Send a back-in-stock alert to Swym.
   * @param {Object} product - Formatted product object.
   * @param {Object} variant - Formatted variant object.
   * @param {String} email - User email to which the notification should be sent.
   *
   * @returns {Promise}
   */
  function sendWatchlist(product, variant, email) {
    const productUrl = product.url.startsWith(location.protocol)
      ? product.url
      : `${location.protocol}${product.url}`

    // Define the email address to subscribe to
    const mediumValue = email
    const medium = 'email'
    const addToMailingList = 1

    const productData = {
      epi: variant?.id, // unique variant ID of the product.
      empi: product?.id, // Product ID or master product ID
      du: productUrl, // canonical product url.
      iu: product?.featuredImage?.src, // image url for email
      pr: variant?.price?.amount, // price
      et: 8, // event type 8 stands for registering a "notify me" event.
    }

    return new Promise((resolve, reject) => {

      /**
       * Send the out of stock alert.
       * @param {string} mediumValue - Value of the medium (currently supported ‘email’, hence email Id)
       * @param {string} medium - What id is collected (currently 'email' supported)
       * @param {Object} productData - Product that is the content the out of stock alert - https://developers.swym.it/docs/request-a-back-in-stock-alert#product
       * @param {Function} onSuccess - Function that gets called after a successful HTTP API call
       * @param {Function} onError - Error callback function called on unsuccessful response
       * @param {number} [addToMailingList=0] - Optional parameter to indicate if the user will be added to the mailing list.
       * Possible values: 1 (opted in) or 0 (opted out). Note: requires mailing list feature to be set up from Swym's end.
       */
      MintSwat.sendWatchlist(
        mediumValue,
        medium,
        productData,
        (response) => {
          resolve(response)
        },
        (error) => {
          reject(error)
        },
        addToMailingList,
      )
    })
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
    getWishlist,
    addToWishlist,
    removeFromWishlist,
    sendWatchlist,
    openBackInStock,
  })
}
